import React, { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from '@mui/styles';
import MuiTheme from './theme';
import { LeftSidebar, PresentationLayout } from './layout-blueprints';
import { Loader } from './shared/components/Loader';
import Dashboard from './pages/Dashboard';
import UserList from './pages/Users/Users/UserList';
import AddUser from './pages/Users/AddUser';
import FormSubmission from './pages/FormSubmission/FormSubmission/FormSubmission';
import ResetPassword from './pages/ForgotPassword/ResetPassword';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import VerifyEmail from './pages/EmailVerification/VerifyEmail';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import SmsTerms from './pages/Legal/SmsTerms';
import PayrollSummary from './pages/PayrollSummary/PayrollSummary';
import Site from './pages/Site/Site/Site';
import AddEditSite from './pages/Site/AddEditSite/AddEditSite';
import BatchDates from './pages/BatchDates/BatchDates/BatchDates';
import PayrollSync from './pages/FormSubmission/PayrollSync/PayrollSync';
import PayrollMapping from './pages/PayrollSummary/PayrollMapping/PayrollMapping';
import ErrorDetails from './pages/FormSubmission/PayrollSync/ErrorDetails';
import Clients from './pages/Clients/Clients/Clients';
import Region from './pages/Region/Region/Region';
import AddEditRegion from './pages/Region/AddEditRegion/AddEditRegion';
import AddEditClient from './pages/Clients/AddEditClients/AddEditClient';
import ClientUsers from './pages/ClientUsers/ClientUsers/ClientUsers';
import AddClientUsers from './pages/ClientUsers/AddClientUsers/AddClientUsers';
import Scheduler from './pages/Scheduler/Scheduler/Scheduler';
import AddScheduler from './pages/Scheduler/AddScheduler/AddScheduler';
import Dispatches from './pages/Dispatches/Dispatches/Dispatches';
import EditDispatch from './pages/Dispatches/EditDispatch/EditDispatch';
import UserProfile from './pages/UserProfile/UserProfile/UserProfile';
import AddUserProfile from './pages/UserProfile/AddUserProfile/AddUserProfile';
import Org from './pages/Org/Org/Org';
import SuperAdminUserProfile from './pages/SuperAdminUserProfile/SuperAdminUserProfile/SuperAdminUserProfile';
import AddSuperAdminUserProfile from './pages/SuperAdminUserProfile/AddUserProfile/AddUserProfile';
import ParentFolder from './pages/Resources/ParentFolder/ParentFolder';
import SubFolder from './pages/Resources/SubFolder/SubFolder';
import File from './pages/Resources/File/File';
import SupportMessages from './pages/SupportMessages/SupportMessages';
import Certificate from './pages/Certificate/Certificate';
import UserCertificate from './pages/Certificate/UserCertificate';
import OrgFolder from './pages/Resources/OrgFolder/OrgFolder';
import TrainingSchedule from './pages/TrainingSchedule/TrainingSchedule/TrainingSchedule';
import AddTrainingSchedule from './pages/TrainingSchedule/AddTrainingSchedule/AddTrainingSchedule';
import Participant from './pages/Participant/Participant/Participant';
import AddParticipant from './pages/Participant/AddParticipant/AddParticipant';
import SiteAssignment from './pages/SiteAssignment/SiteAssignment/SiteAssignment';
import AddSiteAssignment from './pages/SiteAssignment/AddSiteAssignment/AddSiteAssignment';
import Assignments from './pages/Assignments/Assignments';

const Login = lazy(() => import('./pages/Login'));
const RoutesComponent = () => {

  return (
    <ThemeProvider theme={MuiTheme}>
      <AnimatePresence>
        <Suspense
          fallback={
            <div className="d-flex align-items-center vh-100 justify-content-center text-center font-weight-bold font-size-lg py-3">
              <div className="w-50 mx-auto">
                <Loader color="#ff9900" />
              </div>
            </div>
          }>
          <PresentationLayout>
            {/* login */}
          </PresentationLayout>
          <Routes>
            <Route path='/login' element={<Login />} />
            <Route path="/resetpwd/:pwdkey" element={<ForgotPassword />}></Route>
            <Route path="/verifyemail/:verificationkey" element={<VerifyEmail />}></Route>
            <Route path="/privacy" element={<PrivacyPolicy />}></Route>
            <Route path="/sms-terms" element={<SmsTerms />}></Route>
            <Route path="/" element={<Navigate replace to="/login" />} />
            <Route element={<LeftSidebar />}>
              <Route path="/dashboard" element={<Dashboard />}></Route>
              <Route path="/user/list" element={<UserList />}></Route>
              <Route path="/user/add" element={<AddUser />}></Route>
              <Route path="/user/edit/:id" element={<AddUser />}></Route>
              <Route path="/user/reset-password" element={<ResetPassword />}></Route>
              <Route path="/formsubmission/list" element={<FormSubmission />}></Route>
              <Route path="/payrollsummary/list" element={<PayrollSummary />}></Route>
              <Route path="/customer/list" element={<Site />}></Route>
              <Route path="/customer/add" element={<AddEditSite />}></Route>
              <Route path="/customer/edit/:id" element={<AddEditSite />}></Route>
              <Route path="/client/list" element={<Clients />}></Route>
              <Route path="/client/add" element={<AddEditClient />}></Route>
              <Route path="/client/edit/:id" element={<AddEditClient />}></Route>
              <Route path="/region/list" element={<Region />}></Route>
              <Route path="/region/add" element={<AddEditRegion />}></Route>
              <Route path="/region/edit/:id" element={<AddEditRegion />}></Route>
              <Route path="/batchdates/list" element={<BatchDates />}></Route>
              <Route path="/payrollsync/list" element={<PayrollSync />}></Route>
              <Route path="/payroll/mapping" element={<PayrollMapping />}></Route>
              <Route path="/qb/error/:id" element={<ErrorDetails />}></Route>
              <Route path="/client/users/list" element={<ClientUsers />}></Route>
              <Route path="/client/users/add" element={<AddClientUsers />}></Route>
              <Route path="/client/users/edit/:id" element={<AddClientUsers />}></Route>
              <Route path="/scheduler" element={<Scheduler />}></Route>
              <Route path="/scheduler/add" element={<AddScheduler />}></Route>
              <Route path="/scheduler/edit/:id" element={<AddScheduler />}></Route>
              <Route path="/dispatches/list/:id" element={<Dispatches />}></Route>
              <Route path="/dispatches/edit/:id" element={<EditDispatch />}></Route>
              <Route path="/userProfile/list" element={<UserProfile />}></Route>
              <Route path="/userProfile/add" element={<AddUserProfile />}></Route>
              <Route path="/userProfile/edit/:email/:id" element={<AddUserProfile />}></Route>
              <Route path="/user/profile/list" element={<SuperAdminUserProfile />}></Route>
              <Route path="/user/profile/add" element={<AddSuperAdminUserProfile />}></Route>
              <Route path="/user/profile/edit/:id" element={<AddSuperAdminUserProfile />}></Route>
              <Route path="/org/list" element={<Org />}></Route>
              <Route path="/resources/org/folder/list" element={<OrgFolder />}></Route>
              <Route path="/resources/folder/list" element={<ParentFolder />}></Route>
              <Route path="/resources/subfolder/list/:id" element={<SubFolder />}></Route>
              <Route path="/resources/file/list/:id" element={<File />}></Route>
              <Route path="/support/messages/list" element={<SupportMessages />}></Route>
              <Route path="/certificate/list" element={<Certificate />}></Route>
              <Route path="/user/certificate/list/:userEmail" element={<UserCertificate />}></Route>
              <Route path="/schedule" element={<TrainingSchedule />}></Route>
              <Route path="/schedule/add" element={<AddTrainingSchedule />}></Route>
              <Route path="/schedule/edit/:id" element={<AddTrainingSchedule />}></Route>
              <Route path="/schedule/view/:id" element={<AddTrainingSchedule />}></Route>
              <Route path="/schedule/training/:id" element={<Participant />}></Route>
              <Route path="/schedule/training/:id/add" element={<AddParticipant />}></Route>
              <Route path="/schedule/training/:id/edit/:scheduleId" element={<AddParticipant />}></Route>
              <Route path="/site/assignment" element={<SiteAssignment />}></Route>
              <Route path="/site/assignment/add" element={<AddSiteAssignment />}></Route>
              <Route path="/site/assignment/edit/:id" element={<AddSiteAssignment />}></Route>
              <Route path="/assignments" element={<Assignments />}></Route>
            </Route>
          </Routes>
        </Suspense>
      </AnimatePresence>
    </ThemeProvider>
  );
};

export default RoutesComponent;
